import { Pipe, PipeTransform } from '@angular/core';
import { PCP } from '@zipari/shared-api-models';
import { CareCardConfig } from '@zipari/shared-ds-util-care-card';
import { formatTypes } from '@zipari/shared-ds-util-format';
import { ModalConfig } from '@zipari/shared-ds-util-modal';
import {
  LinkConfig,
  NavigationLinkTargets,
} from '@zipari/shared-ds-util-navigation';
import { FormattingService } from '../../shared/services/formatting.service';
import { copyText } from './care-card.constants';

@Pipe({
  name: 'formatCareCardName',
})
export class FormatCareCardNamePipe implements PipeTransform {
  transform(name: PCP['pcp_name']): string {
    const pcpName = name.full_name
      ? name.full_name
      : `${name.prefix || ''} ${name.first_name || ''} ${
          name.last_name || ''
        } ${name.suffix || ''}`;

    return pcpName.replace('&npsp;', ' '); // Names must break or the layout breaks
  }
}

@Pipe({
  name: 'formatCareCardChangeText',
})
export class FormatCareCardChangeTextPipe implements PipeTransform {
  transform(pcp: PCP, config: CareCardConfig): string {
    const lengthOfPCPInitials = 3;
    const capitalizedPCP =
      pcp.type?.length <= lengthOfPCPInitials
        ? pcp.type.toUpperCase()
        : pcp.type;

    return pcp.type?.toUpperCase() === 'DENTIST'
      ? config.change_dentist_text || `Change ${pcp.type || copyText.dentist}`
      : config.change_pcp_text || `Change ${capitalizedPCP || copyText.pcp}`;
  }
}

@Pipe({
  name: 'formatCareCardPhoneNumber',
})
export class FormatCareCardPhoneNumberPipe implements PipeTransform {
  constructor(private formattingService: FormattingService) {}

  transform(phoneNumber: string): string {
    return phoneNumber
      ? this.formattingService.restructureValueBasedOnFormat(phoneNumber, {
          format: formatTypes.PHONE,
        })
      : '';
  }
}

@Pipe({
  name: 'filterCareCardDentists',
})
export class FilterCareCardDentistsPipe implements PipeTransform {
  transform(pcps: PCP[], config: CareCardConfig) {
    const filteredPcps = pcps.filter(
      (pcp) =>
        (!config.show_dental && pcp.type?.toUpperCase() !== 'DENTIST') ||
        config.show_dental,
    );

    return filteredPcps;
  }
}

@Pipe({
  name: 'careCardLinkConfig',
})
export class CareCardLinkConfigPipe implements PipeTransform {
  transform(
    config: CareCardConfig,
    providerTpe: string,
    textToDisplay: string,
  ): LinkConfig {
    const linkConfig = {
      label:
        providerTpe === 'dentist'
          ? config.add_dentist_text || copyText.addDentist
          : config.add_pcp_text || copyText.addPcp,
      route:
        config.route || config.dental_route
          ? providerTpe === 'dentist'
            ? config.dental_route || config.route
            : config.route
          : '',
      target: config.target === '_self' ? null : NavigationLinkTargets.new_tab,
      externalLink:
        config.href || config.dental_href
          ? providerTpe === 'dentist'
            ? config.dental_href || config.href
            : config.href
          : '',
      useExternalSiteModal: config.use_external_modal,
      bold: true,
    };

    if (textToDisplay) {
      linkConfig.route = `${linkConfig.route}?action=add`;
    }

    return linkConfig;
  }
}

@Pipe({
  name: 'careCardExternalLinkConfig',
})
export class CareCardExternalLinkConfigPipe implements PipeTransform {
  transform(config: CareCardConfig, title): ModalConfig {
    const externalLinkConfig = {
      title: title || copyText.modalTitle,
      description:
        config.external_modal_description || copyText.modalDescription,
      footerCancelButton: config.external_modal_cancel || copyText.cancel,
      footerProceedButton: config.external_modal_proceed || copyText.proceed,
    };

    return new ModalConfig(externalLinkConfig);
  }
}

@Pipe({
  name: 'formatCareCardTitle',
})
export class FormatCareCardTitlePipe implements PipeTransform {
  transform(pcp: PCP, config: CareCardConfig): string {
    const dental_title = config.dental_title
      ? config.dental_title
      : copyText.dentistTitle;
    const pcp_title = config.pcp_title ? config.pcp_title : copyText.pcpTitle;
    const care_card_title =
      pcp.type?.toLowerCase() === copyText.pcp.toLowerCase()
        ? pcp_title
        : dental_title;

    return care_card_title;
  }
}
